import { LayoutBand } from '@fs/zion-ui'
import React, { lazy, type ReactNode, Suspense, useEffect } from 'react'
import { useHistory } from '@fs/zion-router'
import { type TreeGroupDetails } from '@fs/zion-tree-types'
import { useEvent } from '@fs/zion-frontend-friends'
import { useUser } from '@fs/zion-user'
import { useGroupsContext, usePendingInvites } from '@fs/zion-tree-groups'
import PendingInvitesProvider from '../providers/PendingInvitesProvider'
import useGroup from '../services/useGroup'
import { useTreeSettingsParams } from '../new-settings/util'
import TreeGroupDetailsProvider from '../new-settings/providers/TreeGroupDetailsProvider'
import SettingsContent, { TabSettingsContentSkeleton } from '../new-settings/components/SettingsContent'
import useGroupMembers from '../services/useGroupMembers'
import MembersProvider from '../providers/MembersProvider'
import { useUserIsDataAdmin } from '../providers/DataAdminProvider'
import StartingPersonProvider from '../providers/StartingPersonProvider'
import GedcomDataProviderSmart from '../providers/GedcomDataProviderSmart'
import usePrivatePersonCount from '../services/usePrivatePersonCount'
import PrivatePersonCountProvider from '../providers/PrivatePersonCountProvider'
import { GedcomInfoSheetProvider } from '../new-settings/providers/GedcomInfoSheetProvider'
import SkeletonAnimationSyncDecorator from '../components/SkeletonAnimationSyncDecorator'

const UnauthorizedPage = lazy(() => import('./UnauthorizedPage'))

function TreeSettingsPage(): JSX.Element | null {
  const history = useHistory()
  const { currentTreeId, selectTree, treeDetails, loading: loadingGroupsContext } = useGroupsContext()
  const { groupId } = useTreeSettingsParams()
  const {
    data: { members: allMembers, pendingMembers },
    refetch: refetchGroupMembers,
    loading: loadingGroupMembers,
    error: errorGroupMembers,
  } = useGroupMembers(groupId)
  const { userIsDataAdmin } = useUserIsDataAdmin()
  const user = useUser()
  const currentMemberDetails = allMembers?.find((member) => member.memberCisId === user.cisId)
  const { data: group, refetch: refetchGroupDetails, loading: loadingGroup } = useGroup<TreeGroupDetails>(groupId)

  const treeId = group?.treeIds?.[0]
  const { privatePersonCount, refetchPrivatePersonCount } = usePrivatePersonCount(treeId)
  const onLeaveGroup = useEvent(() => {
    history.push('/trees')
  })

  const getPendingInvites =
    !!group &&
    (currentMemberDetails || userIsDataAdmin) &&
    (currentMemberDetails?.status === 'ADMIN' || userIsDataAdmin)

  const { singleUseInvites, multiUseInvites, refetchInvites } = usePendingInvites({
    groupId: group?.id,
    willFetch: getPendingInvites,
  })

  useEffect(() => {
    const isPartOfGroup = Boolean(
      currentMemberDetails && (currentMemberDetails?.status === 'MEMBER' || currentMemberDetails?.status === 'ADMIN')
    )
    // This sets the current group's treeId as the session's treeId, making sure that the user belongs to the group
    if (treeId && (isPartOfGroup || userIsDataAdmin) && currentTreeId !== treeId && selectTree) {
      selectTree(treeId)
    }
  }, [currentMemberDetails, currentTreeId, selectTree, treeId, userIsDataAdmin])

  if (
    (!group && loadingGroup) ||
    (allMembers.length === 0 && loadingGroupMembers) ||
    currentTreeId !== treeId ||
    loadingGroupsContext
  ) {
    return <TabSettingsContentSkeleton />
  }

  if (!group || errorGroupMembers?.response?.status === 403) {
    return (
      <Suspense fallback="">
        <UnauthorizedPage />
      </Suspense>
    )
  }

  if (treeDetails?.hidden) {
    // Trees are hidden while still uploading, and their settings are not linked anywhere in the UI.
    // A user with direct knowledge of the groupId could navigate here manually, so we render
    // nothing to avoid exposing settings for an incomplete tree.
    return null
  }

  return (
    <PrivatePersonCountProvider
      privatePersonCount={privatePersonCount}
      refetchPrivatePersonCount={refetchPrivatePersonCount}
    >
      <StartingPersonProvider treeId={treeId} types={group.types}>
        <TreeGroupDetailsProvider group={group} refetchGroup={refetchGroupDetails} onLeaveGroup={onLeaveGroup}>
          <GedcomDataProviderSmart treeId={treeId}>
            <MembersProvider
              allMembers={allMembers}
              pendingMembers={pendingMembers}
              currentMemberDetails={currentMemberDetails}
              refetchGroupMembers={refetchGroupMembers}
              userIsDataAdmin={userIsDataAdmin}
            >
              <PendingInvitesProvider
                singleUseInvites={singleUseInvites}
                refetchInvites={refetchInvites}
                multiUseInvites={multiUseInvites}
                group={group}
              >
                <GedcomInfoSheetProvider>
                  <SettingsContent />
                </GedcomInfoSheetProvider>
              </PendingInvitesProvider>
            </MembersProvider>
          </GedcomDataProviderSmart>
        </TreeGroupDetailsProvider>
      </StartingPersonProvider>
    </PrivatePersonCountProvider>
  )
}

export default function TreeSettingsPageWrapper(): JSX.Element {
  return (
    <TreeSettingsPageDecorator>
      <TreeSettingsPage />
    </TreeSettingsPageDecorator>
  )
}

function TreeSettingsPageDecorator({ children }: { children: ReactNode }): JSX.Element {
  return <LayoutBand marginY="sm">{children}</LayoutBand>
}

export function SettingsPageSkeleton(): JSX.Element {
  return (
    <SkeletonAnimationSyncDecorator>
      <TreeSettingsPageDecorator>
        <TabSettingsContentSkeleton />
      </TreeSettingsPageDecorator>
    </SkeletonAnimationSyncDecorator>
  )
}
